import { useEffect, useMemo, useState } from 'react';
import { Flex, ScrollView } from '@banhala/ui-kit';
import { isNil, isNotNil } from 'shared/src/utils';
import type { GoodsOption } from 'shared/src/api';
import type { OptionComponent } from 'shared/src/api/goodsService/getGoodsOptions/getGoodsOptions';

import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
import { useGoodsOptions } from '../../queries/useGoodsOptions';
import { useGoodsOptionsPricing } from '../../queries/useGoodsOptionsPricing';
import { useLogViewLastDepthGoodsOptionModal } from '../../hooks/useLogViewLastDepthGoodsOptionModal';

import {
  InlineOptionGroupList,
  InlineSelectedAttributeList,
} from './InlineOptionGroupList';
import { InlineQuantityRow } from './InlineQuantityRow';
import { InlinePriceSummary } from './InlinePriceSummary';
import { InlineActionButtons } from './InlineActionButtons';
import {
  computeInlineOptionState,
  type SelectedByDepth,
} from './inlineOptionStateUtils';

const pickAutoSelect = (
  components: OptionComponent[],
  firstGoodsOptionSno: number | null | undefined,
): OptionComponent | undefined => {
  if (components.length === 0) return undefined;
  if (isNotNil(firstGoodsOptionSno)) {
    const matched = components.find(
      c =>
        c.sno === firstGoodsOptionSno ||
        c.goods_option?.sno === firstGoodsOptionSno ||
        c.goods_option_sno === firstGoodsOptionSno,
    );
    if (matched) return matched;
  }
  return components[0];
};

export const InlineOptionView = ({
  onComplete,
}: {
  onComplete: (action: 'checkout' | 'addToCart') => void;
}) => {
  const { data: brief } = useGoodsOptionsBrief();

  const [selected, setSelected] = useState<SelectedByDepth>({});
  const [count, setCount] = useState<number>(1);

  const { data: depth1Response, isFetching: isDepth1Fetching } =
    useGoodsOptions({ depth: 1 });
  const firstDepthGoodsOptionSno =
    brief?.option_groups[0]?.first_goods_option_sno;
  const depth2SelectedSno =
    selected[1]?.first_leaf_option_sno ??
    selected[1]?.goods_option_sno ??
    firstDepthGoodsOptionSno;
  const { data: depth2Response, isFetching: isDepth2Fetching } =
    useGoodsOptions({
      depth: 2,
      selected_option_sno: depth2SelectedSno,
    });
  const isOptionSyncing = isDepth1Fetching || isDepth2Fetching;
  const initialGoodsOptionSno =
    brief?.option_groups[(brief?.option_groups.length ?? 0) - 1]
      ?.first_goods_option_sno;
  useGoodsOptionsPricing({
    options: isNotNil(initialGoodsOptionSno)
      ? [{ sno: initialGoodsOptionSno, count: 1 }]
      : [],
  });

  const depth1Components = useMemo(
    () => depth1Response?.option_components ?? [],
    [depth1Response],
  );
  const depth2Components = useMemo(
    () => depth2Response?.option_components ?? [],
    [depth2Response],
  );

  const isFinalDepth = useMemo(
    () => depth2Components.some(option => option.is_final_depth),
    [depth2Components],
  );

  useLogViewLastDepthGoodsOptionModal({
    isFinalDepth,
    options: depth2Components,
  });

  useEffect(() => {
    if (selected[1] || depth1Components.length === 0) return;
    const target = pickAutoSelect(depth1Components, firstDepthGoodsOptionSno);
    if (target) setSelected(prev => ({ ...prev, 1: target }));
  }, [depth1Components, firstDepthGoodsOptionSno, selected]);

  useEffect(() => {
    if (selected[2] || depth2Components.length === 0) return;
    const firstSno = brief?.option_groups[1]?.first_goods_option_sno;
    const target = pickAutoSelect(depth2Components, firstSno);
    if (target) setSelected(prev => ({ ...prev, 2: target }));
  }, [depth2Components, brief, selected]);

  const groups = useMemo(() => {
    if (!brief) return [];
    return brief.option_groups.map(g => ({
      depth: g.depth,
      hint: g.name,
      selectedComponent: selected[g.depth],
    }));
  }, [brief, selected]);

  const candidatesByDepth = useMemo(
    (): { [depth: number]: OptionComponent[] } => ({
      1: depth1Components,
      2: depth2Components,
    }),
    [depth1Components, depth2Components],
  );

  const optionState = useMemo(
    () =>
      computeInlineOptionState({
        optionGroupsLength: brief?.option_groups.length ?? 0,
        selected,
        count,
      }),
    [brief?.option_groups.length, count, selected],
  );

  useEffect(() => {
    setCount(1);
  }, [optionState.lastOptionSno]);

  useEffect(() => {
    if (!optionState.isAllSelected) return;
    setCount(prev => Math.min(prev, optionState.stepperMaxCount));
  }, [optionState.isAllSelected, optionState.stepperMaxCount]);

  const selectedForOrder: GoodsOption[] = useMemo(
    () =>
      optionState.finalGoodsOption
        ? [{ ...optionState.finalGoodsOption, purchase_count: count }]
        : [],
    [optionState.finalGoodsOption, count],
  );

  const displayState = useMemo(() => {
    const showSelectedAttributes =
      groups.length > 0 &&
      groups.every(group => {
        const candidates = candidatesByDepth[group.depth] ?? [];
        return isNotNil(group.selectedComponent) && candidates.length <= 1;
      });
    const lowStockCount =
      optionState.isAllSelected &&
      !optionState.isSoldOut &&
      optionState.lastComponent?.delivery_type !== 'shak' &&
      optionState.finalGoodsOption &&
      optionState.finalGoodsOption.stock >= 1 &&
      optionState.finalGoodsOption.stock <= 9
        ? optionState.finalGoodsOption.stock
        : null;
    const showAllSingleStockLabel =
      showSelectedAttributes && isNotNil(lowStockCount);

    return {
      lowStockCount,
      showAllSingleStockLabel,
      showSelectedAttributes,
    };
  }, [
    candidatesByDepth,
    optionState.finalGoodsOption,
    groups,
    optionState.isAllSelected,
    optionState.isSoldOut,
    optionState.lastComponent?.delivery_type,
  ]);

  const { lowStockCount, showAllSingleStockLabel, showSelectedAttributes } =
    displayState;

  const handleSelect = (component: OptionComponent) => {
    setSelected(prev => {
      const next: SelectedByDepth = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (Number(key) < component.depth) next[Number(key)] = value;
      });
      next[component.depth] = component;
      return next;
    });
  };

  const handleCompleteRestockNotification = () => {
    setSelected(prev => {
      const lastSelected = prev[optionState.lastDepth];
      if (isNil(lastSelected?.goods_option)) return prev;

      return {
        ...prev,
        [optionState.lastDepth]: {
          ...lastSelected,
          goods_option: {
            ...lastSelected.goods_option,
            is_applied_for_restock: true,
          },
        },
      };
    });
  };

  if (!brief) return null;

  return (
    <Flex flex={1}>
      <ScrollView style={{ flex: 1 }}>
        <Flex padding={{ top: 16 }}>
          {showSelectedAttributes ? (
            <InlineSelectedAttributeList groups={groups} />
          ) : (
            <InlineOptionGroupList
              groups={groups}
              candidatesByDepth={candidatesByDepth}
              onSelect={handleSelect}
            />
          )}
        </Flex>
        {optionState.isAllSelected && !optionState.isRestockMode && (
          <InlineQuantityRow
            count={count}
            stepperMaxCount={optionState.stepperMaxCount}
            onChangeCount={setCount}
            lowStockCount={showAllSingleStockLabel ? lowStockCount : null}
            topPadding={showSelectedAttributes ? 16 : 0}
          />
        )}
      </ScrollView>
      <Flex borderTopWidth={1} borderColor={'border_tertiary'}>
        <InlinePriceSummary
          selected={selectedForOrder}
          count={count}
          isAllSelected={optionState.isAllSelected}
          isRestockMode={optionState.isRestockMode}
          deliveryType={optionState.lastComponent?.delivery_type}
        />
        <InlineActionButtons
          selected={selectedForOrder}
          isRestockMode={optionState.isRestockMode}
          isSoldOutNoRestock={optionState.isSoldOutNoRestock}
          isPurchasable={optionState.isPurchasable}
          isOptionSyncing={isOptionSyncing}
          isAlreadySubscribed={optionState.isAlreadySubscribed}
          lastGoodsOptionSno={optionState.lastGoodsOptionSno}
          lastDepth={optionState.lastDepth}
          priorOptionSno={optionState.priorOptionSno}
          onCompleteRestockNotification={handleCompleteRestockNotification}
          onComplete={onComplete}
        />
      </Flex>
    </Flex>
  );
};
